import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FaArrowLeft, FaImage, FaXmark, FaGlobe } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { blogService } from '../../services/blogService';
import { showToast } from '../../utils/toast';
import { getBlogImageUrl } from '../../utils/imageUtils';
import './CreateBlog.css';
import '../common/JoditEditor.css';
import RichTextEditor from '../common/RichTextEditor';

const EditBlog = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [blogData, setBlogData] = useState({
    title: '',
    content: '',
    featured_image: null,
    content_blocks: [] // New field for repeater content
  });
  const [imagePreview, setImagePreview] = useState('');

  // Fetch blog data
  useEffect(() => {
    fetchBlogData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Debug state changes
  useEffect(() => {
    console.log('Current blogData state:', blogData);
    console.log('Current imagePreview state:', imagePreview);
  }, [blogData, imagePreview]);

  const fetchBlogData = async () => {
    try {
      const response = await blogService.getBlogByIdAdmin(id);
      const blog = response.blog;

      // Parse content blocks if they exist
      let contentBlocks = [];
      if (blog.content_blocks) {
        try {
          contentBlocks = typeof blog.content_blocks === 'string'
            ? JSON.parse(blog.content_blocks)
            : blog.content_blocks;
        } catch (error) {
          console.error('Error parsing content blocks:', error);
          contentBlocks = [];
        }
      }

      setBlogData({
        title: blog.title || '',
        content: blog.content || '',
        featured_image: blog.image || null, // Store the existing image filename
        content_blocks: contentBlocks
      });

      // Set preview for existing image
      if (blog.image) {
        const imageUrl = getBlogImageUrl(blog.image);
        setImagePreview(imageUrl);
        console.log('Existing featured image:', blog.image);
        console.log('Featured image URL set to:', imageUrl);
        console.log('Environment variable REACT_PATH_FOR__BLOG_IMAGE:', process.env.REACT_PATH_FOR__BLOG_IMAGE);
      } else {
        console.log('No existing featured image found');
      }

      console.log('Fetched blog data:', {
        title: blog.title,
        content: blog.content,
        image: blog.image,
        content_blocks: contentBlocks
      });

      // Debug image URLs
      if (blog.image) {
        console.log('Featured image URL:', `/uploads/blogs/${blog.image}`);
      }

      contentBlocks.forEach((block, index) => {
        if (block.type === 'image' && block.image_filename) {
          console.log(`Content block ${index} image URL:`, `/uploads/blogs/${block.image_filename}`);
        }
      });
    } catch (error) {
      console.error('Error fetching blog:', error);
      showToast.error('Failed to fetch blog data');
      navigate('/admin-dashboard/blog-management');
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (field, value) => {
    setBlogData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Store the actual file object
      handleInputChange('featured_image', file);

      // Create preview for display
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    handleInputChange('featured_image', null);
    setImagePreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!blogData.title.trim()) {
      showToast.error('Please enter a title for the blog post');
      return;
    }

    if (!blogData.content.trim()) {
      showToast.error('Please enter content for the blog post');
      return;
    }

    setLoading(true);
    try {
      await blogService.updateBlog(id, blogData);
      showToast.success('Blog post updated successfully!');
      navigate('/admin-dashboard/blog-management');
    } catch (error) {
      console.error('Error updating blog:', error);
      showToast.error('Failed to update blog post');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="wordpress-admin-page">
      <Container fluid className="px-4 py-3">
        {/* WordPress-style Header */}
        <div className="wp-header">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h1 className="wp-page-title">Edit Post</h1>
              <p className="wp-page-description">Edit your blog post</p>
            </div>
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/admin-dashboard/blog-management')}
              className="wp-back-button"
            >
              <FaArrowLeft className="me-2" />
              Back to Posts
            </Button>
          </div>
        </div>

        <Row>
          {/* Main Content Area */}
          <Col lg={8}>
            <div className="wp-main-content">
              {/* Title Field */}
              <div className="wp-title-field mb-4">
                <input
                  type="text"
                  className="wp-title-input"
                  placeholder="Add title"
                  value={blogData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                />
              </div>

              {/* Jodit Rich Text Editor */}
              <div className="wp-content-editor">
                <RichTextEditor
                  value={blogData.content}
                  onChange={(content) => handleInputChange('content', content)}
                  placeholder="Write your content here..."
                />
              </div>
            </div>
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            <div className="wp-sidebar">
              {/* Publish Box */}
              <div className="wp-publish-box">
                <div className="wp-publish-box-header">
                  <h3>Update</h3>
                </div>
                <div className="wp-publish-box-content">
                  <div className="wp-publish-actions">
                    <Button
                      variant="primary"
                      size="lg"
                      className="wp-publish-button w-100 mb-2"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          Updating...
                        </>
                      ) : (
                        <>
                          <FaGlobe className="me-2" />
                          Update
                        </>
                      )}
                    </Button>

                  </div>
                </div>
              </div>

              {/* Featured Image Box */}
              <div className="wp-featured-image-box">
                <div className="wp-featured-image-box-header">
                  <h3>Featured Image</h3>
                </div>
                <div className="wp-featured-image-box-content">
                  {!imagePreview ? (
                    <div className="wp-image-upload-area-small">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="wp-file-input"
                        id="featured-image"
                      />
                      <label htmlFor="featured-image" className="wp-upload-label-small">
                        <FaImage className="wp-upload-icon-small" />
                        <span>Set featured image</span>
                      </label>
                    </div>
                  ) : (
                    <div className="wp-image-preview-small">
                      <img
                        src={imagePreview}
                        alt="Featured"
                        className="wp-preview-img-small"
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="wp-remove-image-small"
                        onClick={handleRemoveImage}
                      >
                        <FaXmark />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EditBlog; 